// Shared UI components - TIC brand
import { useState } from 'react'

import { tokensFor, currentThemeId } from '../lib/theme'

// `colors` resolves to the REAL values of the active theme (see lib/theme.js).
// Using real values rather than CSS variables keeps canvas charts and SVG
// presentation attributes working. Cached per theme id; busts on theme change.
let _cacheId = null
let _cache = {}
function _tokens() {
  const id = currentThemeId()
  if (id !== _cacheId) { _cacheId = id; _cache = tokensFor(id) }
  return _cache
}
export const colors = new Proxy({}, {
  get(_t, key) { return _tokens()[key] },
})

export const fonts = {
  heading: "'Manrope', sans-serif",
  body:    "'Manrope', sans-serif",
  mono:    "'JetBrains Mono', monospace",
}

// Apple recommended minimum text sizes:
// Primary/body: 17px, Secondary: 15px, Caption: 13px min
// We use: primary 15-16px, secondary 14px, labels 13px min

export function PageHeader({ title, subtitle, action }) {
  return (
    <div style={{
      padding: '14px 16px 12px',
      borderBottom: `1px solid ${colors.borderGold}`,
      background: `linear-gradient(180deg, rgba(201,151,43,0.04) 0%, transparent 100%)`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ fontSize: '32px', color: colors.text, fontWeight: 800, margin: 0, fontFamily: fonts.heading, letterSpacing: '-0.01em' }}>{title}</h1>
          {subtitle && <p style={{ fontSize: '16px', color: colors.muted, fontFamily: fonts.body, fontWeight: 500, margin: '4px 0 0', lineHeight: 1.4 }}>{subtitle}</p>}
        </div>
        {action && <div style={{ flexShrink: 0 }}>{action}</div>}
      </div>
    </div>
  )
}

export function MetricCard({ label, value, delta, deltaPos, style }) {
  return (
    <div style={{
      background: colors.card,
      border: `1px solid ${colors.borderGold}`,
      borderRadius: '12px',
      padding: '16px 18px',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: `0 8px 24px rgba(0,0,0,0.32), 0 0 24px ${colors.cardGlow}`, backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur,
      ...style,
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, width: '3px', height: '100%', background: colors.gold }} />
      <div style={{ fontSize: '16px', color: colors.muted, letterSpacing: '0.03em', fontFamily: fonts.body, fontWeight: 600, marginBottom: '8px', paddingLeft: '8px' }}>
        {label}
      </div>
      <div style={{ fontSize: '32px', color: colors.text, fontWeight: 700, fontFamily: fonts.heading, letterSpacing: '0.02em', paddingLeft: '8px' }}>
        {value}
      </div>
      {delta && (
        <div style={{ fontSize: '14px', fontFamily: fonts.body, marginTop: '5px', color: deltaPos ? colors.green : colors.red, paddingLeft: '8px', fontWeight: 500 }}>
          {delta}
        </div>
      )}
    </div>
  )
}

export function Card({ children, style }) {
  return (
    <div style={{
      background: colors.card,
      border: `1px solid ${colors.borderGold}`,
      borderRadius: '12px',
      padding: '20px 22px',
      boxShadow: `0 8px 24px rgba(0,0,0,0.32), 0 0 24px ${colors.cardGlow}`, backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur,
      ...style,
    }}>
      {children}
    </div>
  )
}

export function CardTitle({ children, action }) {
  return (
    <div style={{
      fontSize: '16px',
      letterSpacing: '-0.01em',
      color: colors.gold,
      fontFamily: fonts.body,
      fontWeight: 700,
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
    }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>{children}</span>
      {action}
    </div>
  )
}

export function GoldButton({ children, onClick, small, style }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: colors.gold,
        border: 'none',
        borderRadius: '10px',
        color: colors.onAccent,
        fontSize: small ? '12px' : '13px',
        fontFamily: fonts.body,
        fontWeight: 700,
        padding: small ? '8px 14px' : '11px 20px',
        cursor: 'pointer',
        letterSpacing: '-0.01em',
        minHeight: '44px',
        ...style,
      }}
    >
      {children}
    </button>
  )
}

export function GhostButton({ children, onClick, small, danger, style }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'transparent',
        border: `1px solid ${danger ? 'rgba(224,82,82,0.4)' : colors.borderGold}`,
        borderRadius: '10px',
        color: danger ? colors.red : colors.muted,
        fontSize: small ? '12px' : '13px',
        fontFamily: fonts.body,
        fontWeight: 600,
        padding: small ? '7px 12px' : '10px 18px',
        cursor: 'pointer',
        letterSpacing: '-0.01em',
        minHeight: '44px',
        ...style,
      }}
    >
      {children}
    </button>
  )
}

export function Input({ label, value, onChange, type = 'text', placeholder, prefix, autoFocus }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      {label && (
        <label style={{ display: 'block', fontSize: '14px', color: colors.muted, fontFamily: fonts.body, fontWeight: 600, marginBottom: '6px' }}>
          {label}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        {prefix && (
          <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: colors.muted, fontFamily: fonts.mono, fontSize: '14px' }}>
            {prefix}
          </span>
        )}
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          style={{
            width: '100%',
            padding: prefix ? '12px 12px 12px 28px' : '12px 14px',
            background: 'rgba(255,255,255,0.04)',
            border: `1px solid rgba(255,255,255,0.1)`,
            borderRadius: '10px',
            color: colors.text,
            fontSize: '16px',
            fontFamily: fonts.body,
            outline: 'none',
            boxSizing: 'border-box',
            minHeight: '48px',
          }}
        />
      </div>
    </div>
  )
}

export function Select({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      {label && (
        <label style={{ display: 'block', fontSize: '14px', color: colors.muted, fontFamily: fonts.body, fontWeight: 600, marginBottom: '6px' }}>
          {label}
        </label>
      )}
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '12px 14px',
          background: colors.navyMid,
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '10px',
          color: colors.text,
          fontSize: '16px',
          fontFamily: fonts.body,
          outline: 'none',
          boxSizing: 'border-box',
          minHeight: '48px',
        }}
      >
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  )
}

export function Modal({ title, children, onClose }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.65)',
      display: 'flex',
      alignItems: isMobile ? 'flex-end' : 'center',
      justifyContent: 'center',
      zIndex: 100,
      backdropFilter: 'blur(3px)',
    }} onClick={onClose}>
      <div style={{
        background: colors.cardMid,
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: isMobile ? '24px 24px 0 0' : '20px',
        padding: isMobile ? '0 0 max(32px, env(safe-area-inset-bottom))' : '0',
        width: '100%',
        maxWidth: isMobile ? '100%' : '480px',
        maxHeight: '90vh',
        overflow: 'auto',
      }} onClick={e => e.stopPropagation()}>
        {isMobile && <div style={{ width: '36px', height: '4px', background: 'rgba(255,255,255,0.15)', borderRadius: '10px', margin: '10px auto 0' }} />}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <span style={{ fontSize: '16px', color: colors.text, fontWeight: 700, fontFamily: fonts.heading, letterSpacing: '-0.01em' }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.muted, cursor: 'pointer', fontSize: '22px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}>×</button>
        </div>
        <div style={{ padding: '16px 20px 20px' }}>
          {children}
        </div>
      </div>
    </div>
  )
}

export function ProgressBar({ pct, color, height = 6 }) {
  const capped = Math.min(pct, 100)
  const barColor = pct > 100 ? colors.red : (color || colors.gold)
  return (
    <div style={{ width: '100%', height, background: 'rgba(255,255,255,0.07)', borderRadius: 0 }}>
      <div style={{ width: `${capped}%`, height: '100%', background: barColor, transition: 'width 0.5s ease' }} />
    </div>
  )
}

export function MonthPicker({ value, onChange }) {
  const months = []
  const now = new Date()
  for (let i = 0; i < 13; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    const label = d.toLocaleString('en-GB', { month: 'long', year: 'numeric' })
    months.push({ value: val, label })
  }
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        background: colors.navyMid,
        border: `1px solid ${colors.borderGold}`,
        borderRadius: '10px',
        color: colors.gold,
        fontSize: '14px',
        fontFamily: fonts.body,
        fontWeight: 600,
        letterSpacing: '0.01em',
        padding: '8px 12px',
        outline: 'none',
        cursor: 'pointer',
        minHeight: '44px',
      }}
    >
      {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
    </select>
  )
}

// Pay-cycle picker. Same shape as MonthPicker but the value is the cycle's
// start-ISO date (YYYY-MM-DD) and the label is the cycle's "end-month" name
// (e.g. "May 2026" for the cycle leading INTO 28 May pay day) plus a small
// date-range subtitle (e.g. "28 Apr - 27 May").
//
// `cycles` is an array of cycle objects from payday.js - the caller pre-builds
// them so this component stays display-only and stateless.
export function CyclePicker({ value, onChange, cycles }) {
  const [open, setOpen] = useState(false)
  if (!cycles || cycles.length === 0) {
    return (
      <div style={{ fontSize: '14px', color: colors.muted, fontFamily: fonts.body, padding: '4px 0' }}>
        Set pay day in Settings to enable period tracking
      </div>
    )
  }
  const selected = cycles.find(c => c.startIso === value) || cycles[0]
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px',
          background: 'rgba(201,151,43,0.08)', border: '1px solid rgba(201,151,43,0.2)',
          borderRadius: '20px', padding: '5px 12px', cursor: 'pointer',
          fontFamily: fonts.body, fontSize: '14px', fontWeight: 600, color: colors.gold,
        }}
      >
        📅 {selected.rangeLabel || selected.label} <span style={{ opacity: 0.6, fontSize: '14px' }}>▾</span>
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: colors.cardMid, width: '100%', maxWidth: '480px', margin: '0 auto',  borderRadius: '24px 24px 0 0', padding: '8px 0 40px', maxHeight: '70vh', overflowY: 'auto' }}>
            <div style={{ width: '36px', height: '4px', background: 'rgba(255,255,255,0.15)', borderRadius: '10px', margin: '8px auto 16px' }} />
            <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '16px', color: colors.text, padding: '0 20px 14px', letterSpacing: '0.02em' }}>Select period</div>
            {cycles.map(c => {
              const active = c.startIso === (value || cycles[0].startIso)
              return (
                <button
                  key={c.startIso}
                  onClick={() => { onChange(c.startIso); setOpen(false) }}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    width: '100%', padding: '14px 20px', background: active ? 'rgba(201,151,43,0.1)' : 'transparent',
                    border: 'none', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer',
                    fontFamily: fonts.body,
                  }}
                >
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: 600, color: active ? colors.gold : colors.text, textAlign: 'left' }}>
                      {c.label}
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, marginTop: '3px', textAlign: 'left' }}>
                      {c.rangeLabel}
                    </div>
                  </div>
                  {active && <span style={{ color: colors.gold, fontSize: '16px' }}>✓</span>}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}

export function fmt(n, currency = 'GBP') {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n || 0)
}

export function fmt2(n, currency = 'GBP') {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0)
}

export function fmtPct(n) {
  return `${Math.round(n || 0)}%`
}

// Ordinal day-of-month: 1 → "1st", 2 → "2nd", 22 → "22nd", 11 → "11th".
export function ordinalDay(n) {
  const d = parseInt(n, 10)
  if (!d || d < 1) return ''
  const s = ['th', 'st', 'nd', 'rd']
  const v = d % 100
  return d + (s[(v - 20) % 10] || s[v] || s[0])
}

export function Loading() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: colors.muted, fontFamily: fonts.body, fontSize: '16px', letterSpacing: '0.01em' }}>
      Loading...
    </div>
  )
}

export function Empty({ message, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '40px 20px', color: colors.muted, fontFamily: fonts.body, fontSize: '14px' }}>
      <div style={{ marginBottom: '14px' }}>{message}</div>
      {action}
        </div>
  )
}

// 44px touch target icon button - Apple recommended
export function IconButton({ icon, onClick, color, danger, label }) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      style={{
        width: '44px', height: '44px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'none', border: 'none', cursor: 'pointer', borderRadius: '10px',
        color: danger ? 'rgba(224,82,82,0.5)' : (color || `rgba(${colors.textRgb},0.4)`),
        flexShrink: 0,
      }}
    >
      <i className={`ti ${icon}`} style={{ fontSize: '16px' }} />
    </button>
  )
}
